import { useMemo, useState, useEffect } from "react";

import { SUPPORTED_MEDIUMS } from "./constants";
import type { SupportedMedium } from "./types";
import type { Frame } from "@/types/dragon-tiles";

/**
 * Hook to manage tile variant filtering and medium selection
 */
export function useFrameMediums(frame: Frame) {
  const [selectedMedium, setSelectedMedium] = useState<SupportedMedium | null>(
    null,
  );

  // Filter variants to only supported mediums
  const supportedMediums = useMemo(() => {
    return frame.frame_variants.filter((variant) =>
      SUPPORTED_MEDIUMS.includes(variant.medium as SupportedMedium),
    );
  }, [frame.frame_variants]);

  // MMS-only frames (no sms variant) should surface as "sms" in the picker
  const hasTextVariant = useMemo(() => {
    if (supportedMediums.some((v) => v.medium === "sms")) return true;
    return frame.frame_variants.some((v) => v.medium === "mms");
  }, [supportedMediums, frame.frame_variants]);

  // Set initial selected medium (prefer email, fallback to first available)
  useEffect(() => {
    const hasAny = supportedMediums.length > 0 || hasTextVariant;
    if (!hasAny) {
      setSelectedMedium(null);
      return;
    }

    const emailVariant = supportedMediums.find((v) => v.medium === "email");
    if (emailVariant) {
      setSelectedMedium(emailVariant.medium as SupportedMedium);
    } else if (supportedMediums.length > 0) {
      setSelectedMedium(supportedMediums[0]?.medium as SupportedMedium);
    } else if (hasTextVariant) {
      setSelectedMedium("sms");
    }
  }, [supportedMediums, hasTextVariant]);

  // Get the currently selected variant
  const selectedVariant = useMemo(() => {
    if (!selectedMedium) return null;
    return supportedMediums.find((v) => v.medium === selectedMedium);
  }, [selectedMedium, supportedMediums]);

  return {
    selectedMedium,
    setSelectedMedium,
    supportedMediums,
    selectedVariant,
  };
}
